import * as React from 'react';

import type { ChatClient, ChatProfile } from '@/api/chat-client';
import { HttpChatClient } from '@/api/chat-client.http';

// =============================================================================
// chat-client-context.tsx — Injection du ChatClient (DI seam, R15-COM-02).
// =============================================================================
// DEUX seams distincts (un par profil) : le préfixe de base et le cookie CSRF sont FIGÉS dans chaque
// client HTTP par défaut. Câblé HTTP DÈS LE DÉPART (garde 09_stub_in_prod_guard : aucun défaut stub-en-
// prod). Les tests de composant injectent un StubChatClient (double déterministe) via le provider adéquat.
//
// `useChatClient(profile)` lit le contexte du bon profil : les hooks TanStack restent paramétrés par profil
// sans jamais choisir eux-mêmes le préfixe/CSRF (l'autorité de cloisonnement est côté API/RLS).

/** Adaptateur ADMIN par défaut : HTTP réel (base `/api/communication/admin`, CSRF `admin_csrf`). */
const defaultAdminChatClient: ChatClient = new HttpChatClient('admin');
/** Adaptateur FOURNISSEUR par défaut : HTTP réel (base `/api/communication`, CSRF `supplier_csrf`). */
const defaultSupplierChatClient: ChatClient = new HttpChatClient('supplier');

const ChatAdminClientContext = React.createContext<ChatClient>(
  defaultAdminChatClient,
);
const ChatSupplierClientContext = React.createContext<ChatClient>(
  defaultSupplierChatClient,
);

export interface ChatClientProviderProps {
  client?: ChatClient | undefined;
  children: React.ReactNode;
}

export function ChatAdminClientProvider({
  client = defaultAdminChatClient,
  children,
}: ChatClientProviderProps): React.JSX.Element {
  return (
    <ChatAdminClientContext.Provider value={client}>
      {children}
    </ChatAdminClientContext.Provider>
  );
}

export function ChatSupplierClientProvider({
  client = defaultSupplierChatClient,
  children,
}: ChatClientProviderProps): React.JSX.Element {
  return (
    <ChatSupplierClientContext.Provider value={client}>
      {children}
    </ChatSupplierClientContext.Provider>
  );
}

/** Lit le ChatClient du profil demandé (admin / supplier) depuis le contexte correspondant. */
export function useChatClient(profile: ChatProfile): ChatClient {
  const admin = React.useContext(ChatAdminClientContext);
  const supplier = React.useContext(ChatSupplierClientContext);
  return profile === 'admin' ? admin : supplier;
}
